import type { FormEvent, FunctionComponent } from 'react';
import { useEffect, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Input } from '../../shared/components/input/input';
import { createClassName, createStyleHelper } from '../../shared/utils/class-names';
import { formatDate, formatFromDateToTimestampEpoc } from '../../shared/utils/date-format';
import { GameTransactionsComponent } from '../components/game-transactions/game-transactions';
import { SportsTransactionsComponent } from '../components/sports-transactions/sports-transactions';
import { WalletTransactionsComponent } from '../components/wallet-transactions/wallet-transactions';
import { useUserTransactions } from '../hooks/use-user-transactions';
import { DateType, TransactionsViewType } from '../transactions.interface';

import styles from './transactions.module.scss';

const style = createStyleHelper(styles, 'transactions');

export interface ITransactionsProps {
    className?: string;
    view: TransactionsViewType;
}

export const TransactionsComponent: FunctionComponent<ITransactionsProps> = ({
    className,
    view,
}) => {
    const [dates, setDates] = useState({
        dateFrom: formatDate(new Date(new Date().setDate(new Date().getDate() - 30)), '-'),
        dateTo: formatDate(new Date(), '-'),
    });

    const dateChange = useCallback(
        (type: DateType, value: string) => {
            if (type === DateType.FROM) {
                setDates({ ...dates, dateFrom: value });
            } else {
                setDates({ ...dates, dateTo: value });
            }
        },
        [dates, setDates],
    );

    const filters = useMemo(
        () => ({
            dateFrom: dates.dateFrom ? formatFromDateToTimestampEpoc(dates.dateFrom) : undefined,
            dateTo: dates.dateTo
                ? formatFromDateToTimestampEpoc(dates.dateTo) + 86400000
                : undefined,
        }),
        [dates.dateFrom, dates.dateTo],
    );
    const {
        feed: viewFeed,
        fetchMore,
        hasMore,
        fetching,
        updateFilters,
        view: currentView,
    } = useUserTransactions();

    useEffect(() => {
        updateFilters(view, filters);
    }, [updateFilters, filters, view]);

    const feedData = view !== currentView ? [] : viewFeed;

    const onDateFromChange = useCallback(
        (ev: FormEvent) => dateChange(DateType.FROM, (ev.target as HTMLInputElement).value),
        [dateChange],
    );
    const onDateToChange = useCallback(
        (ev: FormEvent) => dateChange(DateType.TO, (ev.target as HTMLInputElement).value),
        [dateChange],
    );

    return (
        <div className={createClassName([className, style()])}>
            <div className={style('dates-input')}>
                <div className={style('date-section')}>
                    <FormattedMessage
                        id="account.transactions.date-from"
                        defaultMessage="Date From"
                    />
                    <Input
                        type="date"
                        value={dates.dateFrom}
                        onInput={onDateFromChange}
                        className={style('from-input')}
                    />
                </div>
                <div className={style('date-section')}>
                    <FormattedMessage id="account.transactions.date-to" defaultMessage="Date To" />
                    <Input
                        type="date"
                        value={dates.dateTo}
                        onInput={onDateToChange}
                        className={style('to-input')}
                    />
                </div>
            </div>

            {view === TransactionsViewType.DEPOSITS || view === TransactionsViewType.WITHDRAWALS ? (
                <WalletTransactionsComponent
                    activityData={feedData}
                    onLoadMore={fetchMore}
                    hasMore={hasMore}
                    fetching={fetching ?? true}
                />
            ) : null}

            {view === TransactionsViewType.GAMEPLAY ? (
                <GameTransactionsComponent
                    activityData={feedData}
                    onLoadMore={fetchMore}
                    hasMore={hasMore}
                    fetching={fetching ?? true}
                />
            ) : null}

            {view === TransactionsViewType.SPORTS ? (
                <SportsTransactionsComponent
                    activityData={feedData}
                    onLoadMore={fetchMore}
                    hasMore={hasMore}
                    fetching={fetching ?? true}
                />
            ) : null}
        </div>
    );
};
